import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { fetchSkutkiUsunieciaCentrum, usunCentrum } from '@/lib/cost-centers-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  USUNIĘCIE CENTRUM KOSZTOWEGO — okno ze skutkami i potwierdzeniem (03.11.2026).
 *
 *  ⚠️⚠️ ZAMÓWIONE (Mateusz): usunąć kod ze WSZYSTKICH miejsc naraz — przynależności, `Users.COSTCENTER`
 *  i słownika. Kaskada i jej kolejność są w API (`usuniecie-centrum.ts`); okno mówi LICZBAMI, co
 *  zniknie, zanim ktoś kliknie — tak jak usuwanie grupy pracowników.
 *
 *  ⚠️ HARMONOGRAMY I HR+ TO OSTRZEŻENIA, NIE BLOKADY: raport filtrujący po kodzie dalej działa dla
 *  przeszłych okresów, a HR+ przywróci kod tylko wtedy, gdy dalej go wysyła — o tym trzeba wiedzieć,
 *  ale nie ma powodu tego zabraniać.
 *
 *  ⚠️ HR+ PRZYWRACA KOD WYŁĄCZNIE NA KARTACH i tylko przy „Wpisuj nowe MPK" (`zakladajMpk`): plan
 *  synchronizacji niczego nie zakłada w `UserCostCenters`, a bez tej opcji kod spoza słownika i spoza
 *  kart pomija (`plan.ts`, `mpkSpozaSlownika`). Po usunięciu kod nie jest już ani tu, ani tu.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function UsuniecieCentrum({ groupId, kod, onClose }: { groupId: string; kod: string; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const skutki = useQuery({
    queryKey: ['cost-centers', 'usuniecie', groupId, kod],
    queryFn: () => fetchSkutkiUsunieciaCentrum(groupId, kod),
  });

  const usun = useMutation({
    mutationFn: () => usunCentrum(groupId, kod),
    onSuccess: () => {
      toast.success(t('cc.usun.zlecono'));
      void qc.invalidateQueries({ queryKey: ['cost-centers'] });
      void qc.invalidateQueries({ queryKey: ['employees'] });
      void qc.invalidateQueries({ queryKey: ['changes'] });
      onClose();
    },
  });

  const s = skutki.data;
  const blad = usun.error ?? skutki.error;

  return (
    <Dialog open onOpenChange={(v) => !v && !usun.isPending && onClose()}>
      <DialogContent
        title={t('cc.usun.tytul')}
        description={
          <span>
            <span className="font-mono">{s?.kod ?? kod}</span>
            {s?.wSlowniku && s.wSlowniku.name !== '' && <> · {s.wSlowniku.name}</>}
          </span>
        }
      >
        {skutki.isPending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('common.loading')}
          </p>
        ) : s ? (
          <div className="space-y-3 text-sm">
            <p className="flex items-start gap-2 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/10 p-2 text-[13px]">
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--destructive)]" />
              <span>{t('potw.nieodwracalne')}</span>
            </p>
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5">
              <dt className="text-muted-foreground">{t('cc.usun.przynaleznosci')}</dt>
              <dd className="text-right font-medium tabular-nums">{s.przynaleznosci.length}</dd>
              <dt className="text-muted-foreground">{t('cc.usun.napisy')}</dt>
              <dd className="text-right font-medium tabular-nums">{s.napisy.length}</dd>
              <dt className="text-muted-foreground">{t('cc.usun.slownik')}</dt>
              <dd className="text-right font-medium">{s.wSlowniku !== null ? t('cc.usun.slownikTak') : t('cc.usun.slownikNie')}</dd>
            </dl>
            <p className="text-xs text-muted-foreground">{t('cc.usun.historia')}</p>
            {s.harmonogramy.length > 0 && (
              <p className="flex items-start gap-1.5 text-xs text-[var(--warning)]">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                {t('cc.usun.harmonogramy').replace('{lista}', s.harmonogramy.map((h) => h.nazwa).join(', '))}
              </p>
            )}
            {s.hrPlus.wlaczona && s.hrPlus.zakladajMpk && (
              <p className="flex items-start gap-1.5 text-xs text-[var(--warning)]">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                {t('cc.usun.hrPlus')}
              </p>
            )}
            {s.wKolejce && <p className="text-xs text-[var(--destructive)]">{t('cc.usun.wKolejce')}</p>}
          </div>
        ) : null}
        <ModalError>{blad instanceof ApiError ? blad.message : blad ? t('common.error') : null}</ModalError>
        <DialogFooter>
          <Button type="button" variant="outline" autoFocus disabled={usun.isPending} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={s === undefined || s.wKolejce || usun.isPending}
            onClick={() => usun.mutate()}
          >
            {usun.isPending ? <Loader2 className="animate-spin" /> : <Trash2 />}
            {t('cc.usun.potwierdz')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
